/** Out of `i18n.ts`, which reaches the route tree and SST resources that no unit test can import. */
import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { translateChildSegments, translateSlug, type SlugContext } from './slug-rewrites';

/**
 * Off the route ids, which the rewrite already resolved, so the switcher cannot disagree. Per context because a
 * merged map is lossy: `about-us` is an `en` slug on both www and the archived site.
 */
function slugContextOf(router: { state: { matches: ReadonlyArray<{ routeId: string }> } }): SlugContext {
	const inGroup = (group: string) =>
		router.state.matches.some(({ routeId }) => routeId === `/${group}` || routeId.startsWith(`/${group}/`));
	return inGroup('www') ? 'www' : inGroup('old') ? 'old' : 'portal';
}

export function buildLanguagePath(
	lang: PortalLocale,
	pathname: string,
	search: string,
	currentLocale: string,
	portalId: string,
	router: { options: { context?: unknown }; state: { matches: ReadonlyArray<{ routeId: string }> } },
	currentCampaignId?: string,
): string {
	const segments = pathname.split('/').filter(Boolean);
	const isLocaleFirst = (portalLocales as readonly string[]).includes(segments[0] ?? '');
	const restSegments = isLocaleFirst ? segments.slice(1) : segments;

	let newCampaignSlug: string | undefined;
	if (currentCampaignId) {
		const qc = (router.options.context as { queryClient?: { getQueryData(key: unknown[]): unknown } } | undefined)
			?.queryClient;
		if (qc) {
			const cached = qc.getQueryData([portalId, currentLocale, 'campaigns', currentCampaignId]) as
				| { slugsByLocale?: Record<string, string>; id?: string }
				| undefined;
			newCampaignSlug = cached?.slugsByLocale?.[lang] ?? cached?.id ?? currentCampaignId;
		}
	}

	const context = slugContextOf(router);
	const newLegalSlug =
		!currentCampaignId && restSegments[0] ? translateSlug(restSegments[0], lang, context) : undefined;
	// Else `/es/contacto/ong/consulta` keeps Spanish child words in a French URL the rewrite cannot resolve.
	const newChildSegments =
		newLegalSlug !== undefined && restSegments[0]
			? translateChildSegments(restSegments[0], restSegments.slice(1), lang, context)
			: [];

	const newRestSegments =
		newCampaignSlug !== undefined
			? [newCampaignSlug, ...restSegments.slice(1)]
			: newLegalSlug !== undefined
				? [newLegalSlug, ...newChildSegments]
				: restSegments;

	return '/' + [lang, ...newRestSegments].join('/') + search;
}
